import React, { useState, useEffect } from "react";
import { login } from "../firebase/auth"; // Import the login function
import { useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth"; // Import Firebase Auth
import { isSyntheticStudentEmail } from "../utils/studentLogin";
import { dashboardPathFor } from "../utils/accountRole";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // "Forgot your password?" — emails a reset link (Firebase's own reset page)
  const [resetMsg, setResetMsg] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          // This used to branch on userRole.includes("educator") and so on,
          // with an alert("Unknown user role.") on the else. Registration
          // writes role: "unpaid" and keeps the real answer in intendedRole,
          // so EVERY account that hadn't yet been promoted by the Stripe
          // webhook fell through to that alert — or, once someone removed it,
          // to /dashboard. That is why an educator kept landing on the
          // student dashboard. dashboardPathFor() reads the granted role
          // first and falls back to signup intent.
          navigate(dashboardPathFor(userDoc.data()));
        } else {
          console.error("No user document found.");
          alert("Failed to fetch your account. Please try again.");
        }
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = await login(email, password); // Log in the user
      const userRef = doc(db, "users", user.uid); // Reference to Firestore document
      const userDoc = await getDoc(userRef); // Get user document

      if (userDoc.exists()) {
        // Same fix as the onAuthStateChanged branch above — see the comment
        // there for why the role field alone can't answer this.
        navigate(dashboardPathFor(userDoc.data()));
      } else {
        console.error("No user document found.");
        alert("Failed to fetch your account. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error.message); // Debugging
      alert(error.message); // Display error message if login fails
    }
  };

  return (
    <div style={styles.container}>
      {/* Logo — doubles as the way back out of the login page.
          "/" renders PublicHome for a signed-out visitor (see App.js); a
          visitor who is already signed in gets forwarded to their dashboard
          from there, which is the same thing the header logo does elsewhere.
          It's a real button rather than a clickable <img> so it is reachable
          by keyboard and announced as a control. */}
      <button
        type="button"
        onClick={() => navigate("/")}
        style={styles.logoButton}
        title="Go to the Smart Board Learning home page"
        aria-label="Go to the Smart Board Learning home page"
      >
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="Smart Board Learning"
          style={styles.logo}
        />
      </button>

      {/* Header */}
      <h1 style={styles.header}>Existing Members—Log In Here</h1>

      {/* Login Form */}
      <div style={styles.loginContainer}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Member Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            placeholder="Email"
            required
          />
          <label style={styles.label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            placeholder="Password"
            required
          />
          <button type="submit" style={styles.button}>
            Log In
          </button>
          <button
            type="button"
            style={styles.forgot}
            disabled={resetBusy}
            onClick={async () => {
              setResetMsg(null);
              const addr = email.trim();
              if (!addr) {
                setResetMsg({ ok: false, text: "Type your email above first, then press this again." });
                return;
              }
              if (isSyntheticStudentEmail(addr)) {
                setResetMsg({ ok: false, text: "This is a class login — ask your teacher to reset the password." });
                return;
              }
              setResetBusy(true);
              try {
                await sendPasswordResetEmail(auth, addr);
              } catch (err) {
                // never reveal whether an email has an account
                console.warn("Password reset:", err?.code || err);
              } finally {
                setResetBusy(false);
                setResetMsg({ ok: true, text: `If ${addr} has an account, a reset link is on its way. Check your inbox (and spam).` });
              }
            }}
          >
            {resetBusy ? "Sending…" : "Forgot your password?"}
          </button>
          {resetMsg && <p style={resetMsg.ok ? styles.resetOk : styles.resetBad}>{resetMsg.text}</p>}
        </form>
      </div>

      {/* Learn More Section */}
      <div style={styles.learnMoreContainer}>
        <h2>Not a Member Yet?</h2>
        <button
          onClick={() => (window.location.href = "https://smartboardlearning.com")}
          style={styles.learnMoreButton}
        >
          Learn More
        </button>
      </div>
    </div>
  );
};

const styles = {
  forgot: { marginTop: 10, background: "none", border: 0, color: "#2563eb", textDecoration: "underline", cursor: "pointer", fontSize: 14, alignSelf: "center" },
  resetOk: { color: "#15803d", fontSize: 14, margin: "8px 0 0", textAlign: "center" },
  resetBad: { color: "#b91c1c", fontSize: 14, margin: "8px 0 0", textAlign: "center" },
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f4f8fc",
    minHeight: "100vh",
    padding: "20px",
  },
  logoButton: {
    display: "block",
    margin: "0 auto",
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
  },
  header: {
    fontSize: "24px",
    color: "#333",
    margin: "20px 0",
  },
  loginContainer: {
    backgroundColor: "#fff",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    borderRadius: "10px",
    padding: "20px",
    maxWidth: "400px",
    margin: "20px auto",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "16px",
    color: "#333",
    marginBottom: "8px",
    textAlign: "left",
  },
  input: {
    padding: "10px",
    fontSize: "16px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    marginBottom: "15px",
  },
  button: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
  learnMoreContainer: {
    marginTop: "30px",
  },
  learnMoreButton: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
};

export default Login;
